/**
 * Расчёт метрик набора.
 *
 * WPM считается по стандартной формуле: пять введённых символов равны одному
 * условному слову (ТЗ §1.3). Длительность берётся по монотонным часам.
 *
 * Число нажатий хранится отдельно от длины набранного текста (ТЗ FR-EN-78):
 * Backspace укорачивает текст, но сделанного нажатия не отменяет, и без этого
 * различия точность нескольких прогонов подряд считалась бы по разным
 * основаниям.
 *
 * На сервер уходят четыре величины — WPM, CPM, точность и число исправлений
 * (ТЗ FR-EN-79). Формат не менялся намеренно: лишнее поле в агрегате — это
 * лишнее, что придётся хранить и объяснять.
 */

import { CHARACTERS_PER_WORD, EMPTY_METRICS, type TypingMetrics } from '@type2x/shared-contracts';

const MS_PER_MINUTE = 60_000;

/** Сырые счётчики, из которых выводятся метрики. */
export interface MetricCounters {
  readonly typedCharacters: number;
  readonly correctCharacters: number;
  readonly corrections: number;
  /** Сколько нажатий сделано всего, включая стёртые потом. */
  readonly keystrokes: number;
  /** Сколько нажатий попало с первого раза. */
  readonly firstTryCorrect: number;
  readonly elapsedMs: number;
  /** Межклавишные интервалы, мс. Используются для оценки ритма. */
  readonly intervals: readonly number[];
}

/** Медиана набора чисел. Возвращает 0 для пустого набора. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle]!;
  return (sorted[middle - 1]! + sorted[middle]!) / 2;
}

/** Перцентиль набора чисел методом ближайшего ранга. */
export function percentile(values: readonly number[], fraction: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.ceil(fraction * sorted.length);
  const index = Math.min(Math.max(rank - 1, 0), sorted.length - 1);
  return sorted[index]!;
}

/**
 * Стабильность ритма: 1 — идеально ровный темп, 0 — предельно рваный.
 *
 * Используется коэффициент вариации интервалов, приведённый к 0..1. Он не
 * зависит от абсолютной скорости, поэтому медленный ровный набор не выглядит
 * хуже быстрого рваного.
 */
export function rhythmStability(intervals: readonly number[]): number {
  const usable = intervals.filter((value) => value > 0);
  if (usable.length < 2) return 0;

  const mean = usable.reduce((sum, value) => sum + value, 0) / usable.length;
  if (mean === 0) return 0;

  const variance = usable.reduce((sum, value) => sum + (value - mean) ** 2, 0) / usable.length;
  const coefficient = Math.sqrt(variance) / mean;

  return Math.max(0, Math.min(1, 1 - coefficient));
}

/** Вычисляет метрики из счётчиков. */
export function computeMetrics(counters: MetricCounters): TypingMetrics {
  const { typedCharacters, correctCharacters, corrections, elapsedMs, intervals } = counters;
  const { keystrokes, firstTryCorrect } = counters;
  if (typedCharacters === 0 || elapsedMs <= 0) {
    return { ...EMPTY_METRICS, corrections, typedCharacters, correctCharacters };
  }

  const minutes = elapsedMs / MS_PER_MINUTE;
  const cpm = correctCharacters / minutes;

  return {
    wpm: cpm / CHARACTERS_PER_WORD,
    cpm,
    accuracy: correctCharacters / typedCharacters,
    // Знаменатель — все нажатия, а не оставшиеся в тексте: исправленное
    // нажатие было сделано, и точность набора о нём помнит.
    firstTryAccuracy: keystrokes === 0 ? 1 : firstTryCorrect / keystrokes,
    keystrokes,
    typedCharacters,
    correctCharacters,
    corrections,
    rhythmStability: rhythmStability(intervals),
    elapsedMs,
  };
}
